import PropTypes from "prop-types";
import * as React from "react";
import { connect } from "react-redux";
import _truncate from "lodash/truncate";
import Dropzone from "react-dropzone";
import ReactTooltip from "react-tooltip";
import Icon from "../Icon";
import { FaCopy, FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";
import { notifyError } from "../../modules/notifications/actions";
import styles from "../../styles/components/UploadCard.css";

export const FILE_TYPES = ["image/png", "image/x-png", "image/jpeg"];
export const MAX_FILE_SIZE = 10000000; // bytes (10MB)

function RequirementText({ name, value }) {
    return (
        <p className={styles.requirement}>
            <span className={styles.requirementName}>{name}</span>
            <span className={styles.requirementValue}>{value}</span>
        </p>
    );
}

RequirementText.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
};

function UploadRequirementsTooltip() {
    return (
        <ReactTooltip
            id="upload-requirements"
            place="bottom"
            border
            type="light"
            effect="solid"
        >
            <div className={styles.uploadCardTooltip}>
                <RequirementText name="Max Pages:" value="6" />
                <RequirementText name="Max size per page:" value="50 MB" />
                <RequirementText name="File Formats:" value="PNG, JPG/JPEG" />
                <p className={styles.requirement}>
                    A minimum width of 800px is suggested for best results.
                </p>
            </div>
        </ReactTooltip>
    );
}

function UploadRequirements({ isFull }) {
    const className = `${styles.uploadRequirements} ${isFull && styles.large}`;
    return (
        <p data-tip="" data-for="upload-requirements" className={className}>
            <span className={styles.label}>Upload Requirements</span>
            <FaInfoCircle />
        </p>
    );
}

UploadRequirements.propTypes = {
    isFull: PropTypes.bool.isRequired
};

function ErrorMessage({ error }) {
    return (
        <p className={styles.errorMessage}>
            <FaExclamationTriangle /> {error}
        </p>
    );
}

ErrorMessage.propTypes = {
    error: PropTypes.string.isRequired
};

function CardContentIcon({ rejected }) {
    if (rejected) {
        return (
            <FaExclamationTriangle
                className={styles.errorIcon}
            />
        );
    }
    return <FaCopy className={styles.copyIcon} />;
}

CardContentIcon.propTypes = {
    rejected: PropTypes.bool.isRequired
};

function CardContentText({ rejected, isFull }) {
    const className = `${styles.contentText} ${
        isFull ? styles.large : styles.small
    }`;
    if (rejected) {
        return (
            <p className={className}>
                We're sorry. Your file doesn't meet our requirements.
            </p>
        );
    }
    return (
        <p className={className}>
            Drag and drop pages to upload or
            <a className={styles.link}>browse</a>
        </p>
    );
}

CardContentText.propTypes = {
    rejected: PropTypes.bool.isRequired
};

function UploadCardContent({ isDragReject, isDragAccept, isFull }) {
    const className = [
        styles.cardContent,
        isFull && styles.isFull,
        isDragReject && styles.reject,
        isDragAccept && styles.accept
    ].join(" ");

    return (
        <div className={className}>
            <div className={styles.content}>
                <CardContentIcon rejected={isDragReject} />
                <CardContentText rejected={isDragReject} isFull={isFull} />
                <UploadRequirements isFull={isFull} />
            </div>
        </div>
    );
}

UploadCardContent.propTypes = {
    isDragReject: PropTypes.bool.isRequired,
    isDragAccept: PropTypes.bool.isRequired,
    isFull: PropTypes.bool.isRequired
};

class UploadCard extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            error: null
        };
    }

    onDrop = files => {
        if (files.length > this.props.remainingPages) {
            this.setState({
                error: "Too many pages selected."
            });
        } else {
            files.forEach((file, index) => {
                const sortOrder = index + this.props.sortOrderStart;
                this.props.createPage(file, sortOrder);
            });
        }
    };

    onDropRejected = rejectedFiles => {
        rejectedFiles.forEach(file => {
            const fileName = _truncate(file.name, { length: 30 });

            if (file.size > MAX_FILE_SIZE) {
                this.props.notifyFileRejection(
                    `The file '${fileName}' is too large. The max file size is 10MB.`
                );
            } else if (!FILE_TYPES.includes(file.type)) {
                this.props.notifyFileRejection(
                    `The file '${fileName}' is an invalid type. The accepted file types are PNG, JPG/JPEG.`
                );
            } else {
                this.props.notifyFileRejection(
                    `The file '${fileName}' could not be uploaded. The file must be under 10MB, and of type PNG, JPG/JPEG.`
                );
            }
        });
    };

    render() {
        // Dropzone creates a URL for the dropped files via window.URL.createObjectURL() by default.
        // Those URLs are a headache because they're managed by a global object, which means they
        // don't get garbage collected until you explicitly destroy them via window.URL.revokeObjectURL().
        // Since we don't use them anyway, I'll ask Dropzone not to create them in the first place by
        // setting disablePreview = true
        return (
            <div className={styles.uploadCard}>
                {this.state.error &&
                    this.props.isFull && (
                        <ErrorMessage error={this.state.error} />
                    )}
                <Dropzone
                    className={styles.dropzone}
                    disablePreview={true}
                    onDropAccepted={this.onDrop}
                    onDropRejected={this.onDropRejected}
                    maxSize={MAX_FILE_SIZE}
                    multiple={true}
                    accept={FILE_TYPES.join(",")}
                >
                    {({ isDragActive, isDragReject, isDragAccept }) => (
                        <UploadCardContent
                            isDragAccept={isDragAccept}
                            isDragReject={isDragReject}
                            isFull={this.props.isFull}
                        />
                    )}
                </Dropzone>
                {this.state.error &&
                    !this.props.isFull && (
                        <ErrorMessage error={this.state.error} />
                    )}
                <UploadRequirementsTooltip />
            </div>
        );
    }
}

UploadCard.propTypes = {
    isFull: PropTypes.bool.isRequired,
    remainingPages: PropTypes.number.isRequired,
    sortOrderStart: PropTypes.number.isRequired,
    createPage: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch => {
    return {
        notifyFileRejection: message =>
            dispatch(notifyError(message, FaExclamationTriangle))
    };
};

export default connect(
    null,
    mapDispatchToProps
)(UploadCard);



// WEBPACK FOOTER //
// ./src/components/CreateTest/UploadCard.js